import { Component, createElement, useSyncExternalStore, useEffect, useState, type ComponentType, type ComponentProps, type ReactNode } from "react";

/** Only failed imports are tagged as chunk failures; a component's own TypeError is a render error. */
export class ViewLoadError extends Error {
  constructor(cause: unknown) { super("View module unavailable", { cause }); }
}
/** The preloader and mounted views share one promise, including failures (manual document reload recovers those). */
export function deferredView<T extends ComponentType<any>>(loader: () => Promise<{ default: T }>, retryLoader?: () => Promise<{ default: T }>) {
  let pending: ReturnType<typeof loader> | undefined;
  let retried = false;
  let snapshot: { module?: Awaited<ReturnType<typeof loader>>; error?: ViewLoadError } = {};
  const listeners = new Set<() => void>();
  const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
  const getSnapshot = () => snapshot;
  const publish = (next: typeof snapshot) => { snapshot = next; for (const listener of listeners) listener(); };
  const preload = () => pending ??= loader().then((module) => { publish({ module }); return module; }, (cause: unknown) => {
    const error = new ViewLoadError(cause); publish({ error }); throw error;
  });
  /** A new URL bypasses browsers' cached failed dynamic-import result. */
  const retry = () => {
    if (!retryLoader || retried) return Promise.reject(snapshot.error);
    retried = true;
    pending = retryLoader().then((module) => { publish({ module }); return module; }, (cause: unknown) => {
      const error = new ViewLoadError(cause); publish({ error }); throw error;
    });
    publish({});
    return pending;
  };
  // One stable wrapper and module cache. A resolved module renders synchronously. While pending, use the
  // same delayed status directly: React 19's Suspense reveal throttle must not hold a just-ready route.
  const View = (props: ComponentProps<T>) => {
    const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    useEffect(() => { void preload().catch(() => {}); }, []);
    if (state.error) return <section className="ipd-card" role="alert">
      <h2>This view could not be loaded</h2>
      <p>{retryLoader && !retried ? "Try loading this view again." : "Reload the page to try again, or choose another page from the navigation."}</p>
      <button type="button" className="ipd-btn" onClick={() => { if (retryLoader && !retried) void retry().catch(() => {}); else window.location.reload(); }}>{retryLoader && !retried ? "Try again" : "Reload page"}</button>
    </section>;
    return state.module ? createElement(state.module.default, props) : <LoadingView />;
  };
  return { View, preload, retry };
}

function LoadingView() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const timer = setTimeout(() => setVisible(true), 180); return () => clearTimeout(timer); }, []);
  // Mount the empty live region before updating its text. Reserve its line to avoid a loading-text jump.
  return <p role="status" aria-live="polite" style={{ minHeight: "1.5em" }}>{visible ? "Loading view…" : ""}</p>;
}

type Props = { children: ReactNode; resetKey?: string };
type State = { error: unknown; resetKey: string | undefined };
/** Reset errors on navigation without remounting a healthy screen (notably Insights' disclosure and filters). */
export class DeferredView extends Component<Props, State> {
  override state: State = { error: null, resetKey: this.props.resetKey };
  static getDerivedStateFromError(error: unknown) { return { error }; }
  static getDerivedStateFromProps(props: Props, state: State) {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }
  override render() {
    if (this.state.error) return <section className="ipd-card" role="alert">
      <h2>This view encountered a problem</h2>
      <p>Reload the page to try again, or choose another page from the navigation.</p>
      <button type="button" className="ipd-btn" onClick={() => window.location.reload()}>Reload page</button>
    </section>;
    return this.props.children;
  }
}
